import React from "react";

import AnalyticsTracking from "Utils/AnalyticsTracking";

import { useModal } from "Cobalt/Modal";
import type { ModalProps, TriggerProps } from "Cobalt/Modal/StandaloneTrigger";
import AgentHR from "Common/Agent/AgentHR";
import Button from "Common/Button";
import { RoutedBackButton } from "Common/forms/buttons";
import { Div } from "Common/HTML";
import DentalPlanDetails from "Common/plans/DentalPlanDetails";
import VisionPlanDetails from "Common/plans/VisionPlanDetails";

import ChooseCoverageModal from "./Components/ChooseCoverageModal";
import PlanHeader from "./Components/PlanHeader";
import PriceTable from "./Components/PriceTable";
import MedicalPlanDetails from "./EmployeeSimplePlanDetails";

import type { FamilyInfo } from "./Utils/chooseCoverage";
import type { Plan, ContributionFormula, FamilyQuote, EmployeeForm } from "Utils/types";

type Props = {
  prices: FamilyQuote;
  familyInfo: Array<FamilyInfo>;
  plan: Plan;
  contributions: ContributionFormula;
  backLink?: string;
  readonly onClickBack?: () => void;
  employeeForm?: EmployeeForm;
};

const MedicalBottomLinkButton = (props: TriggerProps) => (
  <Button
    size="medium"
    text="Is this plan right for me?"
    color="link"
    data-keyboard="true"
    pl={0}
    onClick={(_e) => {
      AnalyticsTracking.trackEvent("recommendation_modal_clicked", {
        location: "employee_application_choose_coverage_page",
      });
    }}
    {...props}
  />
);

const PlanDetails = ({ plan, prices, contributions, familyInfo }) => {
  const { type } = plan;
  if (type === "medical") {
    return <MedicalPlanDetails plan={plan} className="MedicalPlanDetails--table" prices={prices} contribution={contributions} familyInfo={familyInfo} />;
  }
  if (type === "dental") {
    return <DentalPlanDetails striped condensed={false} plan={plan} className="DentalPlanDetails--table" />;
  }
  if (type === "vision") {
    return <VisionPlanDetails striped condensed={false} plan={plan} className="VisionPlanDetails--table" />;
  }
  return null;
};

const EmployeeSinglePlan = ({ plan, prices, contributions, familyInfo, backLink, onClickBack, employeeForm }: Props) => {
  const { show, hide, isShowing } = useModal();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    show();
  };
  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });
    hide();
  };

  return (
    <div className="employee-single-plan-detail">
      <PlanHeader
        plan={plan}
        button={<Button className="select-plan float-right" size="medium" text="Enroll or Decline" color="blue" data-keyboard="true" onClick={openModal} />}
      />
      <ChooseCoverageModal plan={plan} contributions={contributions} familyInfo={familyInfo} prices={[prices]} openModal={isShowing} closeModal={closeModal} />
      <div className="employee-single-simple-plan-details">
        <PriceTable plan={plan} prices={prices} familyInfo={familyInfo} contributions={contributions} />
        <PlanDetails plan={plan} prices={prices} contributions={contributions} familyInfo={familyInfo} employeeForm={employeeForm} openModal={openModal} />
      </div>
      <Div className="clearfix" pb={4}>
        <AgentHR />
        <RoutedBackButton href={backLink} onClick={onClickBack} />
        <Button
          size="medium"
          color="blue"
          className="select-plan center-xs-block"
          text="Enroll or Decline"
          data-keyboard="true"
          onClick={openModal}
          style={{ float: "right" }}
        />
      </Div>
    </div>
  );
};

export default EmployeeSinglePlan;
